// Display helpers for the admin notifications bell/dropdown/page. Mirrors
// the icon/label vocabulary already used in pages/history/historyPage.jsx
// so notifications and activity history read consistently.


export const ACTION_TYPE_CONFIG = {
  // The first four used to name action-* classes that no stylesheet defines, so
  // every channel notification rendered with an uncoloured icon. type-* is the
  // vocabulary NotificationsPage.css and the history page actually ship.
  synced:                  { icon: "fas fa-rotate",          cssClass: "type-updated"   },
  sync_failed:             { icon: "fas fa-triangle-exclamation", cssClass: "type-rejected" },
  connected:               { icon: "fas fa-plug",            cssClass: "type-approved"  },
  disconnected:            { icon: "fas fa-plug-circle-xmark", cssClass: "type-rejected" },
  quarantined:             { icon: "fas fa-circle-question", cssClass: "type-created"   },
  published:               { icon: "fas fa-store",           cssClass: "type-approved"  },
  approved:                { icon: "fas fa-check-circle",    cssClass: "type-approved"  },
  rejected:                { icon: "fas fa-times-circle",    cssClass: "type-rejected"  },
  created:                 { icon: "fas fa-plus-circle",     cssClass: "type-created"   },
  updated:                 { icon: "fas fa-edit",            cssClass: "type-updated"   },
  cancelled:               { icon: "fas fa-ban",             cssClass: "type-cancelled" },
  paid:                    { icon: "fas fa-money-bill-wave", cssClass: "type-paid"      },
  completed:               { icon: "fas fa-check-double",    cssClass: "type-completed" },
  refunded:                { icon: "fas fa-rotate-left",     cssClass: "type-paid"      },
  shipped:                 { icon: "fas fa-truck",           cssClass: "type-completed" },
  topped_up:               { icon: "fas fa-wallet",          cssClass: "type-paid"      },
  deducted:                { icon: "fas fa-money-bill-wave", cssClass: "type-cancelled" },
  subscribed:              { icon: "fas fa-star",            cssClass: "type-approved"  },
  purchased:               { icon: "fas fa-cart-shopping",   cssClass: "type-paid"      },
  requested:               { icon: "fas fa-paper-plane",     cssClass: "type-created"   },
  requested_cancellation:  { icon: "fas fa-ban",             cssClass: "type-cancelled" },
  requested_deletion:      { icon: "fas fa-trash",           cssClass: "type-cancelled" },
  priced:                  { icon: "fas fa-tag",             cssClass: "type-paid"      },
  deleted:                 { icon: "fas fa-trash",           cssClass: "type-rejected"  },
  // Subscription reminders, written by the cron sweep and the quota hooks in
  // backend/helpers/subscription_reminders.php. Both use type-created for the
  // same reason `quarantined` above does: amber is this app's "waiting on you"
  // colour, and that is exactly what these are -- nothing has gone wrong yet,
  // but it will unless the seller acts.
  renewal_due:             { icon: "fas fa-calendar-day",    cssClass: "type-created"   },
  quota_low:               { icon: "fas fa-gauge-high",      cssClass: "type-created"   },
};

export const prettify = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const ENTITY_LABELS = {
  // `sales_channel` is what log_activity() is called with; `channel` was a guess that
  // nothing ever writes. Both are kept: the label map is a lookup with a prettify()
  // fallback, so a stale key costs nothing while a missing one shows a raw slug.
  sales_channel: "Sales Channel",
  channel: "Sales Channel",
  channel_order: "Channel Order",
  channel_job: "Sync Task",
  product_link: "Product Link",
  listing_draft: "Store Listing",
  channel_config: "Store Setup",
  listing_stock: "Listing Stock",
  order_tracking: "Order Tracking",
  order: "Order",
  sourcing_request: "Sourcing Request",
  restock_request: "Restock Request",
  product: "Product",
  client: "Client",
  wallet: "Wallet",
  subscription: "Subscription",
  credits: "Credits",
  account: "Seller Account",
  top_up_request: "Top-Up Request",
  cancellation_request: "Cancellation Request",
  deletion_request: "Deletion Request",
};

export const ENTITY_TYPE_OPTIONS = [
  { value: "order",            label: "Orders"             },
  { value: "sourcing_request", label: "Sourcing Requests"  },
  { value: "restock_request",  label: "Restock Requests"   },
  { value: "product",          label: "Inventory"          },
  { value: "client",           label: "End-Customers"      },
  { value: "wallet",           label: "Wallet"             },
  { value: "top_up_request",   label: "Top-Up Requests"    },
  { value: "credits",          label: "Credits"            },
  { value: "subscription",     label: "Subscription"       },
  // No store categories: connected-store messages are not on the Notifications
  // page any more -- they have their own section on the homepage.
];

/**
 * Connected-store messages. They are kept OUT of the bell and the Notifications
 * page (the backend filters them) and shown in the homepage's Store alerts
 * section instead. Mirrors admin_store_alert_entity_types() in
 * backend/helpers/notification_helper.php -- change both together.
 */
export const STORE_ALERT_ENTITY_TYPES = new Set([
  "sales_channel", "channel_job", "channel_config", "product_link",
  "listing_draft", "order_tracking", "channel_order", "listing_stock",
]);

export const isStoreAlert = (entity_type) => STORE_ALERT_ENTITY_TYPES.has(entity_type);

// entity_id arrives base64 from the server for every type (every record id the
// app sees is base64), and every list takes ?highlight= in that same form.
export const buildRelated = (entry) => {
  const { entity_type, entity_id } = entry;
  if (!entity_type) return null;
  switch (entity_type) {
    case "order":            return { label: `Order #${entity_id}`,            link: `/customerOrders?highlight=${entity_id}` };
    case "sourcing_request": return { label: `Sourcing Request #${entity_id}`, link: `/customerRequests?highlight=${entity_id}` };
    case "restock_request":  return { label: `Restock Request #${entity_id}`,  link: `/restock-requests?highlight=${entity_id}` };
    case "product":          return { label: `Product #${entity_id}`,          link: `/customerProducts?highlight=${entity_id}` };
    case "client":           return { label: `Client #${entity_id}`,           link: `/customerList?highlight=${entity_id}` };
    // /customerList is the END-CUSTOMERS page (a seller's own clients), not
    // the sellers list — an account notification pointed there landed nowhere
    // useful. /allSellers is the actual seller directory, and the only place
    // an account-level action (e.g. reinstating a suspended seller) can be
    // taken.
    case "account":          return { label: "Seller Account",                 link: `/allSellers` };
    case "wallet":           return { label: "Wallet",                        link: `/global-ledger` };
    case "top_up_request":   return { label: "Top-Up Request",                link: `/topup-requests` };
    case "credits":          return { label: "Credits Purchase",              link: `/global-ledger` };
    case "subscription":     return { label: "Subscription",                  link: `/subscriptionRequests` };
    // Store integrations go to the page that owns the problem instead of the
    // details modal, which has no view for them (see OPENS_PAGE_ENTITY_TYPES).
    // entity_id is already base64, which is what ?job= and ?channel= expect.
    case "sales_channel":    return { label: "Connected Store",               link: `/integrations` };
    case "channel_job":      return { label: "Sync Task",                     link: `/integrationSyncLog?job=${entity_id}` };
    case "channel_config":   return { label: "Store Setup",                   link: `/integrationSyncLog?channel=${entity_id}` };
    case "order_tracking":   return { label: `Order #${entity_id}`,           link: `/customerOrders?highlight=${entity_id}` };
    case "product_link":
    case "listing_draft":
    case "listing_stock":
    case "channel_order":    return { label: ENTITY_LABELS[entity_type] || prettify(entity_type), link: `/integrationSyncLog` };
    default:                 return { label: ENTITY_LABELS[entity_type] || prettify(entity_type), link: null };
  }
};

// Builds the target for useRelatedOperationModal()'s openRelated() from a
// notification/history row — reuses buildRelated() purely as the "is this
// actionable?" predicate (its `link` field). entityId is handed to the
// modal RAW (as it arrives on the row, base64) — relatedEntityService.js is
// where the base64-vs-raw split per endpoint actually gets resolved.
/**
 * Entity types whose "Related To" link NAVIGATES instead of opening the modal.
 *
 * The modal has no view for a store problem -- it would only repeat the
 * notification's own text -- while each of these has a page that shows it:
 * Connected Stores, or the sync log scoped to the task or store.
 */
export const OPENS_PAGE_ENTITY_TYPES = new Set([
  "sales_channel", "channel_job", "channel_config", "order_tracking",
  "product_link", "listing_draft", "listing_stock", "channel_order",
]);

export const relatedOpensPage = (entity_type) => OPENS_PAGE_ENTITY_TYPES.has(entity_type);

export const toRelatedTarget = (entry) => {
  if (!entry?.entity_type) return null;
  if (!buildRelated(entry)?.link) return null;
  return {
    entityType: entry.entity_type,
    entityId: entry.entity_id,
    fallbackLabel: buildRelated(entry)?.label,
    entry,
  };
};

export const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)     return "just now";
  if (diff < 3600)   { const m = Math.floor(diff / 60);   return `${m} minute${m !== 1 ? "s" : ""} ago`; }
  if (diff < 86400)  { const h = Math.floor(diff / 3600); return `${h} hour${h !== 1 ? "s" : ""} ago`; }
  if (diff < 604800) { const d = Math.floor(diff / 86400);return `${d} day${d !== 1 ? "s" : ""} ago`; }
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Who did it. See the seller app's copy of this helper — same three roles, but
 * an admin reads the third one as "Seller" rather than "You".
 */
export const actorLabel = (role) => {
  if (role === "admin") return "Admin";
  if (role === "system") return "Sourship";
  return "Seller";
};
